import { getDeviceId } from "@/helpers/device";
import store from "@/store";
import axios from "axios";
import { isEuRegion } from "@/helpers/regions";
import { BUNDLE, CUSTOMER, DOMAIN_ORDER, ORDER, SITE_ORDER } from "@/helpers/const";
import smartStorage from "../helpers/smart-storage";
import { getDeviceInfo } from "@/helpers/getDeviceInfo";
import { getDomainName, getSourceClient, returnObjectIfValueExists } from "@/helpers";

let nyxHttpInstance = null;

/**
 * Authenticated client for `/m/addEvent` and `/m/entityChange`. `nyxHttp` sends the token as the
 * `x-a1` header Medusa expects, and inherits `$http`'s interceptors on top of that. URLs are passed
 * in full, so its base URL is never used.
 * Note: those inherited interceptors include the response one, which emits `go-to-login` on a 401.
 *
 * Resolved on first use, not at import: `api-service` reaches back into `@/helpers/http`, which is
 * itself part of an import cycle with this module, so at import time `$http` is still undefined and
 * building `nyxHttp` throws.
 */
function getNyxHttp() {
  if (!nyxHttpInstance) {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    nyxHttpInstance = require("@/services/api-service").nyxHttp;
  }
  return nyxHttpInstance;
}

const FLOCK_ACCOUNT = "flock_account";
const FLOCK_ORDER = "flock_order";
const TITAN_CUSTOMER = "titan_customer";

// Avoiding cyclic dependency so we used the method here instead of helpers/index.js"
const isCustomerEvent = userType => userType === CUSTOMER;
const isOrderEvent = userType =>
  userType === ORDER || userType === DOMAIN_ORDER || userType === SITE_ORDER;
const isBundleEvent = userType => userType === BUNDLE;
function getOS() {
  const deviceInfo = getDeviceInfo();
  return deviceInfo.os.name;
}

function getBrowser() {
  const deviceInfo = getDeviceInfo();
  return deviceInfo.browser.name;
}

function getDevice() {
  return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
}

function getSourceClientProps() {
  return returnObjectIfValueExists("source_client", getSourceClient());
}

const device = getDevice() ? "mobile" : "desktop";
const browser = getBrowser();
const os = getOS();

/**
 * `/m/addEvent` endpoint for the current region. For logged in clients only.
 * @returns {String} The Medusa event URL.
 */
function getEventUrl() {
  return isEuRegion ? process.env.VUE_APP_MEDUSA_AUTH_EU_URL : process.env.VUE_APP_MEDUSA_AUTH_URL;
}

/**
 * `/m/addPreAuthEvent` endpoint for the current region. For unauthenticated clients only.
 * @returns {String} The Medusa pre-auth event URL.
 */
function getPreAuthEventUrl() {
  return isEuRegion ? process.env.VUE_APP_MEDUSA_EU_URL : process.env.VUE_APP_MEDUSA_URL;
}

/**
 * `/m/entityChange` endpoint for the current region.
 * @returns {String} The Medusa entity change URL.
 */
function getEntityChangeUrl() {
  return isEuRegion
    ? process.env.VUE_APP_MEDUSA_ENTITY_CHANGE_EU_URL
    : process.env.VUE_APP_MEDUSA_ENTITY_CHANGE_URL;
}

/**
 * Headers for an explicitly-passed token, in the `x-a1` format Medusa expects — the same shape
 * `nyxHttp` builds from storage.
 * @param {String} token
 * @returns {Object} Request headers.
 */
function getTokenHeaders(token) {
  return { "Content-Type": "application/json", "x-a1": `2;${token}` };
}

function logEvent(eventObj) {
  if (process.env.VUE_APP_ENV_FLAG !== "prod") {
    /* Log events only on staging and development. */
    console.log("Event logged", eventObj);
  }
}

/**
 * Mailbox-scoped events used to be `flock_account` whenever an `email` attribute was present. They
 * are now `flock_order` like every other non-bundle/customer event: the mailbox travels as the
 * `email` attribute instead of as the entity.
 */
export function getEventType(userType) {
  if (isBundleEvent(userType)) {
    return BUNDLE;
  } else if (isCustomerEvent(userType)) {
    return TITAN_CUSTOMER;
  } else if (isOrderEvent(userType)) {
    return userType === ORDER ? FLOCK_ORDER : userType;
  }
  return FLOCK_ORDER;
}

function getEventUserType(userType) {
  switch (userType) {
    case ORDER:
    case DOMAIN_ORDER:
    case SITE_ORDER:
      return ORDER;
  }

  if (isCustomerEvent(userType)) {
    return TITAN_CUSTOMER;
  } else if (isBundleEvent(userType)) {
    return BUNDLE;
  }
  return FLOCK_ACCOUNT;
}

/**
 * Medusa expects `entityId` as a string. Falsy ids collapse to `""` rather than being stringified,
 * so callers still fall back to `dummy_entity_id` instead of sending "NaN"/"0"/"undefined".
 * @param {*} id The raw id, from a store getter or storage.
 * @returns {String} The id as a string, or "" when there isn't one.
 */
export function toEntityId(id) {
  return id ? String(id) : "";
}

/**
 * `flock_order` events are keyed by the mail order id, not by the domain. Callers have historically
 * passed the domain, which is kept only as a fallback for flows that fire before an order is loaded
 * (login, partner provision, purchase). The domain is always available as `attrs.domain_name`.
 * @param {*} fallback Used when there is no order id in the store.
 * @returns {String} The order id as a string.
 */
function getOrderEntityId(fallback) {
  return toEntityId(store.getters["mail/orderId"]) || toEntityId(fallback);
}

export function getEventEntityId(userType, domain) {
  switch (userType) {
    case ORDER:
      return toEntityId(store.getters["mail/orderId"]);

    case DOMAIN_ORDER:
      /* `domain/orderId`, not `domain/domainID`: the latter reads `domainDetails.id`, which is the
         bundle id leaking in from the `bundleDetails` spread in `formatDomainDetails` — the domain
         order's own id lives on `orderId`. */
      return toEntityId(store.getters["domain/orderId"]);

    case SITE_ORDER:
      return toEntityId(store.getters["site/orderId"]);
  }

  if (isCustomerEvent(userType)) {
    return toEntityId(smartStorage.getItem("bllUserId"));
  } else if (isBundleEvent(userType)) {
    return toEntityId(store.getters["bundle/bundleId"]);
  }
  /* Mirrors `getEventType`: everything left over is a `flock_order`, keyed by the order id. */
  return getOrderEntityId(domain);
}

class Medusa {
  /**
   * Builds the event object that `trackEvent`/`trackPreAuthEvent` post to Medusa.
   * Mailbox-scoped events carry the mailbox as the `email` attribute; the entity is always the
   * order (`flock_order`) unless `user_type` makes it a bundle/customer/domain/site event.
   * @param {Object} event The event attributes that you want to send to Medusa
   * @returns {Object} The Medusa event payload.
   */
  createTrackedEventObj(event) {
    const domain = getDomainName();
    const { user_type } = event;
    const type = getEventType(user_type);
    return {
      entityId: getEventEntityId(user_type, domain) || "dummy_entity_id",
      product: "control_panel",
      service: "control_panel",
      type,
      attrs: {
        ...event,
        user_type: getEventUserType(user_type),
        browser,
        device,
        domain_name: domain,
        eventTs: +new Date(),
        os,
        ...getSourceClientProps(),
      },
    };
  }

  /**
   * Creates an event object and track it to Medusa. For logged in users only —
   * use `trackPreAuthEvent` from anything that can run before login.
   * @param {Object} event The event attributes that you want to send to Medusa
   * @param {Object} [options] `{ token }` — see `send`.
   * @returns {Promise} Promise that resovles when events are tracked.
   */
  trackEvent(event, options) {
    return this.send(this.createTrackedEventObj(event), options);
  }

  /**
   * `trackEvent` for clients that are not logged in yet, e.g. the login and partner purchase flows.
   * @param {Object} event The event attributes that you want to send to Medusa
   * @returns {Promise} Promise that resovles when events are tracked.
   */
  trackPreAuthEvent(event) {
    return this.sendPreAuthEvent(this.createTrackedEventObj(event));
  }

  /**
   * Sends an event as a logged in user.
   *
   * By default the token comes from storage via `nyxHttp`. Callers whose token is not in storage —
   * the partner purchase flow keeps it in the `purchaseAuthData` prop — pass it explicitly and the
   * request goes out on bare `axios` instead: `nyxHttp`'s own interceptor would overwrite a
   * per-request `x-a1`, and its inherited 401 handler would log the user out mid-purchase.
   * @param {Object} eventObj The Medusa event payload, usually from `createEventObj`.
   * @param {Object} [options] `{ token }` to authenticate with instead of the stored token.
   * @returns {Promise} Promise that resolves once the event is sent.
   */
  send(eventObj, { token } = {}) {
    const request = token
      ? axios.post(getEventUrl(), eventObj, { headers: getTokenHeaders(token) })
      : getNyxHttp().post(getEventUrl(), eventObj);
    return request
      .then(() => logEvent(eventObj))
      .catch(e => {
        console.error(e);
      });
  }

  /**
   * Sends an event as a client that is not logged in yet. Deliberately uses bare `axios`: there is
   * no token to attach, and no interceptor should act on the response.
   * @param {Object} eventObj The Medusa event payload, usually from `createEventObj`.
   * @returns {Promise} Promise that resolves once the event is sent.
   */
  sendPreAuthEvent(eventObj) {
    return axios
      .post(getPreAuthEventUrl(), eventObj)
      .then(() => logEvent(eventObj))
      .catch(e => {
        console.error(e);
      });
  }

  /**
   * Entity changes are always authenticated. The token comes from storage via `nyxHttp`, or from
   * `options.token` for callers whose token is not in storage — same rule as `send`.
   * @param {Object} eventObj The entity change payload.
   * @param {Object} [options] `{ token }` to authenticate with instead of the stored token.
   * @returns {Promise} Promise that resolves once the change is sent.
   */
  entityChange(eventObj, { token } = {}) {
    const request = token
      ? axios.post(getEntityChangeUrl(), eventObj, { headers: getTokenHeaders(token) })
      : getNyxHttp().post(getEntityChangeUrl(), eventObj);
    return request
      .then(() => {
        if (process.env.VUE_APP_ENV_FLAG !== "prod") {
          /* Log entity change events only on staging and development. */
          console.log("Entity change Event logged", eventObj);
        }
      })
      .catch(e => {
        console.error(e);
      });
  }

  createEventObj(obj, entity, type, hattrs) {
    const eventObj = {
      product: "control_panel",
      service: "control_panel",
      type: type,
      /* `flock_order` callers pass the domain; the order id is what this event is keyed by.
         Mailbox events pass the mailbox as the `email` attribute, not as the entity. */
      entityId:
        (type === FLOCK_ORDER ? getOrderEntityId(entity) : toEntityId(entity)) || "dummy_entity_id",
      attrs: {
        ...obj,
        os: getOS(),
        browser: getBrowser(),
        device: getDevice() ? "mobile" : "desktop",
        device_id: getDeviceId(),
        eventTs: +new Date(),
        domain_name: getDomainName(),
        ...getSourceClientProps(),
      },
    };
    if (hattrs !== undefined) {
      eventObj.hattrs = hattrs;
    }
    return eventObj;
  }
}

export default new Medusa();
